import { useState, type JSX } from 'react';
import {
  NOTIFICATION_CHANNEL_KINDS,
  NOTIFICATION_CHANNEL_KIND_KEYS,
  channelNeedsSecret,
  type MonitorKind,
  type MonitorStatus,
  type NotificationChannel,
  type NotificationChannelKind,
  type NotificationDelivery,
  type NotificationRule,
  type Site,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { Field } from '../../shared/ui/Field';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';
import { StatusDot } from '../../shared/ui/StatusDot';
import type { TFunction } from 'i18next';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { useSites } from '../nodes/api';
import {
  useChannels,
  useCreateChannel,
  useCreateRule,
  useDeleteChannel,
  useDeleteRule,
  useDeliveries,
  useRules,
  useTestChannel,
  useUpdateChannel,
  useUpdateRule,
} from './api';

const STATUS_LABEL_KEYS: Readonly<Record<MonitorStatus, string>> = {
  down: 'notifications.status.down',
  up: 'notifications.status.up',
  unknown: 'notifications.status.unknown',
};

const MONITOR_KINDS: readonly MonitorKind[] = ['icmp', 'tcp', 'http', 'snmp'];

/**
 * Уведомления (§11, фаза 9, задача 2).
 *
 * Три панели в одном разделе, и порядок в них не случаен: канал — куда
 * писать, правило — о чём, журнал — дошло ли. Настраивают их именно
 * в этом порядке, а разбираются в обратном.
 */
export function NotificationsPage(): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const channels = useChannels();
  const rules = useRules();
  const deliveries = useDeliveries();
  const sites = useSites();

  const [addingChannel, setAddingChannel] = useState(false);
  const [editingChannel, setEditingChannel] = useState<NotificationChannel | null>(null);
  const [addingRule, setAddingRule] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);

  const removeChannel = useDeleteChannel();
  const removeRule = useDeleteRule();
  const test = useTestChannel();

  const channelColumns: Column<NotificationChannel>[] = [
    {
      key: 'state',
      header: '',
      width: '20px',
      render: (row) => (
        <StatusDot
          status={row.isEnabled ? 'up' : 'unknown'}
          label={
            row.isEnabled
              ? t('notifications.channelOn', { name: row.name })
              : t('notifications.channelOff', { name: row.name })
          }
        />
      ),
    },
    {
      key: 'name',
      header: t('notifications.column.channel'),
      width: '1.4fr',
      sortValue: (row) => row.name,
      render: (row) => <span className="truncate font-medium">{row.name}</span>,
    },
    {
      key: 'kind',
      header: t('notifications.column.kind'),
      width: '160px',
      sortValue: (row) => row.kind,
      render: (row) => t(NOTIFICATION_CHANNEL_KIND_KEYS[row.kind]),
    },
    {
      key: 'target',
      header: t('notifications.column.target'),
      width: '1.6fr',
      render: (row) => <span className="truncate font-mono text-xs text-dim">{target(row)}</span>,
    },
    {
      key: 'secret',
      header: t('notifications.column.secret'),
      width: '90px',
      render: (row) => (
        <span className="font-mono text-xs text-dim">
          {channelNeedsSecret(row.kind)
            ? row.hasSecret
              ? t('notifications.secretSet')
              : t('notifications.secretMissing')
            : t('common.dash')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '112px',
      align: 'right',
      render: (row) => (
        <span className="inline-flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon="plug"
            disabled={test.isPending}
            aria-label={t('notifications.testAria', { name: row.name })}
            title={t('notifications.testTitle')}
            onClick={() => test.mutate(row.id)}
          />
          <Button
            size="sm"
            variant="ghost"
            icon="pencil"
            aria-label={t('notifications.editAria', { name: row.name })}
            title={t('notifications.editChannel')}
            onClick={() => {
              setAddingChannel(false);
              setEditingChannel(row);
            }}
          />
          <Button
            size="sm"
            variant="danger"
            icon="trash"
            disabled={removeChannel.isPending}
            aria-label={t('notifications.deleteAria', { name: row.name })}
            title={t('notifications.deleteChannel')}
            onClick={() => removeChannel.mutate(row.id)}
          />
        </span>
      ),
    },
  ];

  const ruleColumns: Column<NotificationRule>[] = [
    {
      key: 'state',
      header: '',
      width: '20px',
      render: (row) => (
        <StatusDot
          status={row.isEnabled ? 'up' : 'unknown'}
          label={
            row.isEnabled
              ? t('notifications.ruleOn', { name: row.name })
              : t('notifications.ruleOff', { name: row.name })
          }
        />
      ),
    },
    {
      key: 'name',
      header: t('notifications.column.rule'),
      width: '1.3fr',
      sortValue: (row) => row.name,
      render: (row) => <span className="truncate font-medium">{row.name}</span>,
    },
    {
      key: 'condition',
      header: t('notifications.column.condition'),
      width: '2fr',
      render: (row) => (
        <span className="truncate text-muted">{describeRule(row, sites.data ?? [], t)}</span>
      ),
    },
    {
      key: 'channel',
      header: t('notifications.column.channel'),
      width: '1.2fr',
      sortValue: (row) => row.channelName,
      render: (row) => (
        <span className="truncate">
          {row.channelName}
          <span className="ml-1.5 font-mono text-xs text-faint">
            {t(NOTIFICATION_CHANNEL_KIND_KEYS[row.channelKind])}
          </span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '76px',
      align: 'right',
      render: (row) => (
        <span className="inline-flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            icon="pencil"
            aria-label={t('notifications.editAria', { name: row.name })}
            title={t('notifications.editRule')}
            onClick={() => {
              setAddingRule(false);
              setEditingRule(row);
            }}
          />
          <Button
            size="sm"
            variant="danger"
            icon="trash"
            disabled={removeRule.isPending}
            aria-label={t('notifications.deleteAria', { name: row.name })}
            title={t('notifications.deleteRule')}
            onClick={() => removeRule.mutate(row.id)}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3 p-4">
      <Panel
        title={t('notifications.channels')}
        subtitle={channels.data ? `${String(channels.data.length)}` : undefined}
        actions={
          <Button
            size="sm"
            icon="plus"
            onClick={() => {
              setEditingChannel(null);
              setAddingChannel((value) => !value);
            }}
          >
            Добавить канал
          </Button>
        }
      >
        {addingChannel ? <ChannelForm onDone={() => setAddingChannel(false)} /> : null}
        {editingChannel ? (
          <ChannelForm
            key={editingChannel.id}
            existing={editingChannel}
            onDone={() => setEditingChannel(null)}
          />
        ) : null}

        {test.data ? (
          test.data.ok ? (
            <div className="mx-3.5 my-2 rounded-xs border-l-2 border-l-up bg-overlay px-3 py-2 text-sm">
              {t('notifications.testSent')}
            </div>
          ) : (
            <ErrorState
              title={t('notifications.testFailed')}
              hint={t('notifications.testFailedHint', {
                reason:
                  test.data.error === null
                    ? t('notifications.unknownReason')
                    : messageText(test.data.error, t),
              })}
            />
          )
        ) : null}

        {channels.isPending ? <Loading /> : null}
        {channels.error ? (
          <ErrorState title={t('notifications.channelsFailed')} hint={errorText(channels.error)} />
        ) : null}
        {removeChannel.error ? (
          <ErrorState
            title={t('notifications.deleteChannelFailed')}
            hint={errorText(removeChannel.error)}
          />
        ) : null}

        {channels.data ? (
          <DataTable
            columns={channelColumns}
            rows={channels.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title={t('notifications.noChannels')}
                hint={t('notifications.noChannelsHint')}
                action={
                  <Button icon="plus" onClick={() => setAddingChannel(true)}>
                    {t('notifications.addChannel')}
                  </Button>
                }
              />
            }
          />
        ) : null}
      </Panel>

      <Panel
        title={t('notifications.rules')}
        subtitle={rules.data ? `${String(rules.data.length)}` : undefined}
        actions={
          <Button
            size="sm"
            icon="plus"
            disabled={(channels.data ?? []).length === 0}
            title={(channels.data ?? []).length === 0 ? t('notifications.channelFirst') : undefined}
            onClick={() => {
              setEditingRule(null);
              setAddingRule((value) => !value);
            }}
          >
            {t('notifications.addRule')}
          </Button>
        }
      >
        {addingRule ? (
          <RuleForm
            channels={channels.data ?? []}
            sites={sites.data ?? []}
            onDone={() => setAddingRule(false)}
          />
        ) : null}
        {editingRule ? (
          <RuleForm
            key={editingRule.id}
            existing={editingRule}
            channels={channels.data ?? []}
            sites={sites.data ?? []}
            onDone={() => setEditingRule(null)}
          />
        ) : null}

        {rules.isPending ? <Loading /> : null}
        {rules.error ? (
          <ErrorState title={t('notifications.rulesFailed')} hint={errorText(rules.error)} />
        ) : null}

        {rules.data ? (
          <DataTable
            columns={ruleColumns}
            rows={rules.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title={t('notifications.noRules')}
                hint={t('notifications.noRulesHint')}
              />
            }
          />
        ) : null}
      </Panel>

      <Panel title={t('notifications.deliveries')} subtitle={t('notifications.lastFifty')}>
        {deliveries.isPending ? <Loading /> : null}
        {deliveries.error ? (
          <ErrorState title={t('notifications.logFailed')} hint={errorText(deliveries.error)} />
        ) : null}

        {deliveries.data ? (
          <DataTable
            columns={deliveryColumns(t, language)}
            rows={deliveries.data}
            rowKey={(row) => row.id}
            rowDanger={(row) => row.status === 'failed'}
            empty={
              <EmptyState
                title={t('notifications.noDeliveries')}
                hint={t('notifications.noDeliveriesHint')}
              />
            }
          />
        ) : null}
      </Panel>
    </div>
  );
}

const deliveryColumns = (t: TFunction, language: string): Column<NotificationDelivery>[] => [
  {
    key: 'status',
    header: '',
    width: '20px',
    render: (row) => (
      <StatusDot
        status={row.status === 'sent' ? 'up' : 'down'}
        label={
          row.status === 'sent' ? t('notifications.delivered') : t('notifications.notDelivered')
        }
      />
    ),
  },
  {
    key: 'at',
    header: t('notifications.column.at'),
    width: '0.8fr',
    render: (row) => <span className="font-mono text-muted">{formatMoment(row.at, language)}</span>,
  },
  {
    key: 'rule',
    header: t('notifications.column.rule'),
    width: '1fr',
    render: (row) => (
      <span className="truncate">{row.ruleName ?? t('notifications.ruleDeleted')}</span>
    ),
  },
  {
    key: 'channel',
    header: t('notifications.column.channel'),
    width: '1fr',
    render: (row) => (
      <span className="truncate">{row.channelName ?? t('notifications.channelDeleted')}</span>
    ),
  },
  {
    key: 'subject',
    header: t('notifications.column.subject'),
    width: '2fr',
    render: (row) => <span className="truncate text-muted">{row.subject}</span>,
  },
  {
    key: 'counts',
    header: t('notifications.column.events'),
    width: '110px',
    align: 'right',
    render: (row) => (
      <span className="font-mono text-xs text-dim" title={t('notifications.eventsHint')}>
        {row.eventCount}
        {row.suppressedCount > 0 ? ` · +${row.suppressedCount}` : ''}
      </span>
    ),
  },
  {
    key: 'error',
    header: t('notifications.column.reason'),
    width: '1.4fr',
    render: (row) => (
      <span className="truncate font-mono text-xs text-down-fg">
        {row.error === null ? '' : messageText(row.error, t)}
      </span>
    ),
  },
];

// — Формы ————————————————————————————————————————————————

/**
 * Форма канала. Одна на заведение и правку: поля те же, различается только
 * судьба секрета — при правке пустое поле означает «оставить прежний».
 */
function ChannelForm({
  existing,
  onDone,
}: {
  existing?: NotificationChannel;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const create = useCreateChannel();
  const update = useUpdateChannel();

  const [name, setName] = useState(existing?.name ?? '');
  const [kind, setKind] = useState<NotificationChannelKind>(existing?.kind ?? 'telegram');
  const [secret, setSecret] = useState('');
  const [isEnabled, setEnabled] = useState(existing?.isEnabled ?? true);

  const initial: Record<string, unknown> = existing?.config ?? {};
  const [to, setTo] = useState(Array.isArray(initial['to']) ? initial['to'].join(', ') : '');
  const [chatId, setChatId] = useState(
    typeof initial['chatId'] === 'string' ? initial['chatId'] : '',
  );
  const [silent, setSilent] = useState(initial['silent'] === true);
  const [url, setUrl] = useState(typeof initial['url'] === 'string' ? initial['url'] : '');
  const [sign, setSign] = useState(initial['sign'] === true);

  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  function config(): Record<string, unknown> {
    if (kind === 'email') {
      return {
        to: to
          .split(/[,;\s]+/)
          .map((value) => value.trim())
          .filter((value) => value.length > 0),
      };
    }
    if (kind === 'telegram') {
      return { chatId, silent };
    }
    return { url, sign };
  }

  return (
    <form
      className="flex flex-col gap-3 border-b border-line px-3.5 py-3"
      onSubmit={(event) => {
        event.preventDefault();

        if (existing) {
          update.mutate(
            {
              id: existing.id,
              input: {
                name,
                config: config(),
                isEnabled,
                // Пустое поле — «оставить прежний»: форма правки не показывает
                // токен и вернуть его не может.
                ...(secret.length > 0 ? { secret } : {}),
              },
            },
            { onSuccess: onDone },
          );
          return;
        }

        create.mutate(
          { name, kind, config: config(), isEnabled, ...(secret.length > 0 ? { secret } : {}) },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t('sites.form.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint={t('notifications.form.channelNameHint')}
          required
        />

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-muted">{t('notifications.column.kind')}</span>
          <select
            value={kind}
            disabled={existing !== undefined}
            title={existing ? t('notifications.form.kindFixed') : undefined}
            onChange={(event) => setKind(event.target.value as NotificationChannelKind)}
            className="h-[34px] rounded-sm border border-line-strong bg-raised px-2.5 text-base disabled:opacity-50"
          >
            {NOTIFICATION_CHANNEL_KINDS.map((value) => (
              <option key={value} value={value}>
                {t(NOTIFICATION_CHANNEL_KIND_KEYS[value])}
              </option>
            ))}
          </select>
        </label>

        {kind === 'email' ? (
          <Field
            label={t('notifications.form.recipients')}
            value={to}
            onChange={(event) => setTo(event.target.value)}
            mono
            hint={t('notifications.form.recipientsHint')}
            required
          />
        ) : null}

        {kind === 'telegram' ? (
          <>
            <Field
              label={t('notifications.form.chat')}
              value={chatId}
              onChange={(event) => setChatId(event.target.value)}
              mono
              hint={t('notifications.form.chatHint')}
              required
            />
            <Field
              label={
                existing ? t('notifications.form.newBotToken') : t('notifications.form.botToken')
              }
              type="password"
              reveal
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              hint={
                existing ? t('notifications.form.keepToken') : t('notifications.form.botTokenHint')
              }
              required={existing === undefined}
            />
          </>
        ) : null}

        {kind === 'webhook' ? (
          <>
            <Field
              label={t('nodes.field.address')}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              mono
              hint={t('notifications.form.urlHint')}
              required
            />
            <Field
              label={
                existing
                  ? t('notifications.form.newSigningKey')
                  : t('notifications.form.signingKey')
              }
              type="password"
              reveal
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              hint={t('notifications.form.signingKeyHint')}
            />
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          {t('notifications.form.channelEnabled')}
        </label>

        {kind === 'telegram' ? (
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={silent}
              onChange={(event) => setSilent(event.target.checked)}
            />
            {t('notifications.form.silent')}
          </label>
        ) : null}

        {kind === 'webhook' ? (
          <label className="flex items-center gap-1.5" title={t('notifications.form.signHint')}>
            <input
              type="checkbox"
              checked={sign}
              onChange={(event) => setSign(event.target.checked)}
            />
            {t('notifications.form.sign')}
          </label>
        ) : null}
      </div>

      {error ? <ErrorState title={t('notifications.saveFailed')} hint={errorText(error)} /> : null}

      <div className="flex items-center gap-2">
        <Button type="submit" icon="check" disabled={saving}>
          {existing ? t('common.action.save') : t('common.action.add')}
        </Button>
        <Button type="button" variant="ghost" icon="close" onClick={onDone}>
          {t('common.action.cancel')}
        </Button>
      </div>
    </form>
  );
}

/** Форма правила: условие слева, канал справа. */
function RuleForm({
  existing,
  channels,
  sites,
  onDone,
}: {
  existing?: NotificationRule;
  channels: NotificationChannel[];
  sites: Site[];
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const create = useCreateRule();
  const update = useUpdateRule();

  const [name, setName] = useState(existing?.name ?? '');
  const [channelId, setChannelId] = useState(existing?.channelId ?? channels[0]?.id ?? '');
  const [siteIds, setSiteIds] = useState<string[]>(existing?.siteIds ?? []);
  const [monitorKinds, setMonitorKinds] = useState<MonitorKind[]>(existing?.monitorKinds ?? []);
  const [onStatuses, setStatuses] = useState<MonitorStatus[]>(existing?.onStatuses ?? ['down']);
  const [isEnabled, setEnabled] = useState(existing?.isEnabled ?? true);

  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  return (
    <form
      className="flex flex-col gap-3 border-b border-line px-3.5 py-3"
      onSubmit={(event) => {
        event.preventDefault();
        const input = { name, channelId, siteIds, monitorKinds, onStatuses, isEnabled };

        if (existing) {
          update.mutate({ id: existing.id, input }, { onSuccess: onDone });
          return;
        }
        create.mutate(input, { onSuccess: onDone });
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t('sites.form.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint={t('notifications.form.ruleNameHint')}
          required
        />

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="text-muted">{t('notifications.column.channel')}</span>
          <select
            value={channelId}
            onChange={(event) => setChannelId(event.target.value)}
            className="h-[34px] rounded-sm border border-line-strong bg-raised px-2.5 text-base"
          >
            {channels.map((channel) => (
              <option key={channel.id} value={channel.id}>
                {channel.name} — {t(NOTIFICATION_CHANNEL_KIND_KEYS[channel.kind])}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm text-muted">{t('notifications.form.notifyWhen')}</legend>
        <div className="flex flex-wrap gap-3 text-sm">
          {(['down', 'up', 'unknown'] as MonitorStatus[]).map((status) => (
            <label key={status} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={onStatuses.includes(status)}
                onChange={(event) =>
                  setStatuses((current) =>
                    event.target.checked
                      ? [...current, status]
                      : current.filter((value) => value !== status),
                  )
                }
              />
              {t(STATUS_LABEL_KEYS[status])}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm text-muted">
          {t('nav.sites')}{' '}
          <span className="text-faint">{t('notifications.form.noneMeansAll')}</span>
        </legend>
        <div className="flex flex-wrap gap-3 text-sm">
          {sites.map((site) => (
            <label key={site.id} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={siteIds.includes(site.id)}
                onChange={(event) =>
                  setSiteIds((current) =>
                    event.target.checked
                      ? [...current, site.id]
                      : current.filter((value) => value !== site.id),
                  )
                }
              />
              {site.name}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm text-muted">
          {t('notifications.form.checkKinds')}{' '}
          <span className="text-faint">{t('notifications.form.noneMeansAll')}</span>
        </legend>
        <div className="flex flex-wrap gap-3 font-mono text-sm">
          {MONITOR_KINDS.map((kind) => (
            <label key={kind} className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={monitorKinds.includes(kind)}
                onChange={(event) =>
                  setMonitorKinds((current) =>
                    event.target.checked
                      ? [...current, kind]
                      : current.filter((value) => value !== kind),
                  )
                }
              />
              {kind}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex items-center gap-1.5 text-sm">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        {t('notifications.form.ruleEnabled')}
      </label>

      {onStatuses.length === 0 ? (
        <div className="text-sm text-warn">{t('notifications.form.noTransitions')}</div>
      ) : null}

      {error ? <ErrorState title={t('notifications.saveFailed')} hint={errorText(error)} /> : null}

      <div className="flex items-center gap-2">
        <Button type="submit" icon="check" disabled={saving || onStatuses.length === 0}>
          {existing ? t('common.action.save') : t('common.action.add')}
        </Button>
        <Button type="button" variant="ghost" icon="close" onClick={onDone}>
          {t('common.action.cancel')}
        </Button>
      </div>
    </form>
  );
}

// — Мелочи ————————————————————————————————————————————————

/** Куда пишет канал — одной строкой для таблицы. */
export function target(channel: NotificationChannel): string {
  const config = channel.config;

  if (channel.kind === 'email') {
    const to = config['to'];
    return Array.isArray(to) ? to.join(', ') : '—';
  }
  if (channel.kind === 'telegram') {
    return typeof config['chatId'] === 'string' ? config['chatId'] : '—';
  }
  return typeof config['url'] === 'string' ? config['url'] : '—';
}

/**
 * Условие правила словами.
 *
 * Пустой список означает «любой», и написать это надо прямо: строка «узел
 * упал» без указания площадок читается как недосказанность, а «на всех
 * площадках» — как решение.
 */
export function describeRule(
  rule: Pick<NotificationRule, 'siteIds' | 'monitorKinds' | 'onStatuses'>,
  sites: readonly Site[],
  t: TFunction,
): string {
  const statuses =
    rule.onStatuses.length === 0
      ? t('notifications.rule.nothingHappens')
      : rule.onStatuses
          .map((status) => t(STATUS_LABEL_KEYS[status]))
          .join(t('notifications.rule.or'));

  const where =
    rule.siteIds.length === 0
      ? t('notifications.rule.allSites')
      : t('notifications.rule.atSites', {
          list: rule.siteIds
            .map(
              (id) =>
                sites.find((site) => site.id === id)?.name ?? t('notifications.rule.deletedSite'),
            )
            .join(', '),
        });

  const kinds =
    rule.monitorKinds.length === 0
      ? t('notifications.rule.anyCheck')
      : t('notifications.rule.checks', { list: rule.monitorKinds.join(', ') });

  return t('notifications.rule.sentence', { statuses, where, kinds });
}
